<template>
  <div
    v-if="shouldShowDialer"
    class="dialer-toolbar-container"
    :class="{ 'centered-mode': showAutoDialer, 'crisp-visible': hasCrisp }"
    :style="centeredStyle"
  >
    <!-- Compact Dialer Component -->
    <CompactDialer />

    <!-- CRM Page: Classy toolbar with both buttons -->
    <div
      v-if="showAutoDialer"
      class="dialer-toolbar"
      :class="{ 'toolbar-in-call': isInCall }"
    >
      <!-- Auto Dialer Button - Only visible on CRM page -->
      <AutoDialerButton />

      <!-- Manual Dialer Button -->
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            fab
            class="toolbar-fab dialer-fab"
            :class="{
              'fab-in-call': isInCall,
              'fab-disconnected': connectionStatus === 'disconnected',
            }"
            v-bind="attrs"
            v-on="on"
            @click="toggleDialer"
          >
            <div class="fab-inner" :class="{ 'pulse-ring': isInCall }">
              <v-icon v-if="!isInCall" size="26">mdi-dialpad</v-icon>
              <v-icon v-else size="26">mdi-phone-in-talk</v-icon>
            </div>
            <!-- Connection Status Indicator -->
            <div
              class="connection-indicator"
              :class="`indicator-${connectionStatus}`"
            ></div>
          </v-btn>
        </template>
        <span>{{ isInCall ? "In Call" : "Open Dialer" }}</span>
      </v-tooltip>
    </div>

    <!-- Other Pages: Green FAB button -->
    <v-btn
      v-else
      fab
      class="dialer-fab-simple"
      elevation="6"
      :class="{ 'fab-in-call': isInCall }"
      @click="toggleDialer"
    >
      <v-icon v-if="!isInCall">mdi-dialpad</v-icon>
      <v-icon v-else color="red">mdi-phone-in-talk</v-icon>
      <!-- Connection Status Indicator -->
      <div
        class="connection-indicator"
        :class="`indicator-${connectionStatus}`"
      ></div>
    </v-btn>
  </div>
</template>

<script>
import { mapState } from "vuex";
import CompactDialer from "./CompactDialer.vue";
import AutoDialerButton from "./AutoDialerButton.vue";

export default {
  name: "DialerFab",
  components: {
    CompactDialer,
    AutoDialerButton,
  },
  props: {
    showAutoDialer: {
      type: Boolean,
      default: false,
    },
    sidebarOpen: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isMobile: false,
    };
  },
  computed: {
    ...mapState("dialer", ["connectionStatus", "isInCall", "callDuration"]),
    // Check if PBX features should be locked (subscription expired)
    isPbxLocked() {
      return this.$store.getters.isPbxLocked;
    },
    // Check if PBX is configured
    hasPbxConfigured() {
      return this.$store.getters.hasPbxConfigured;
    },
    // Show dialer only if PBX is configured and not locked
    shouldShowDialer() {
      return this.hasPbxConfigured && !this.isPbxLocked;
    },
    // Check if Crisp is visible based on route meta
    hasCrisp() {
      // Crisp is visible when hideCrisp is NOT true in route meta
      return !this.$route.meta.hideCrisp;
    },
    centeredStyle() {
      // Only apply custom positioning in centered mode (CRM page)
      if (!this.showAutoDialer) {
        return {};
      }
      // On mobile, sidebar is hidden so center normally
      if (this.isMobile) {
        return {};
      }
      // On desktop: sidebar is 270px when open, 0 when closed
      const sidebarWidth = this.sidebarOpen ? 270 : 0;
      const offset = sidebarWidth / 2;
      return {
        left: `calc(50% + ${offset}px)`,
      };
    },
  },
  mounted() {
    this.checkMobile();
    window.addEventListener("resize", this.checkMobile);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.checkMobile);
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
    },
    toggleDialer() {
      this.$store.dispatch("dialer/toggleDialer");
    },
    formatDuration(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, "0")}`;
    },
  },
};
</script>

<style scoped>
/* Main Container - Default: Bottom Right (for all pages except CRM) */
.dialer-toolbar-container {
  position: fixed;
  bottom: 28px;
  right: 24px;
  z-index: 1000;
}

/* When Crisp chat is visible, move up to avoid overlap */
.dialer-toolbar-container.crisp-visible:not(.centered-mode) {
  bottom: 90px;
}

/* Centered Mode - Only on CRM page, centered relative to content area */
/* Position is calculated dynamically via inline style based on sidebar state */
.dialer-toolbar-container.centered-mode {
  right: auto;
  transform: translateX(-50%);
  transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Toolbar - Horizontal layout */
.dialer-toolbar {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 60px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08), 0 8px 40px rgba(0, 0, 0, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.dialer-toolbar:hover {
  box-shadow: 0 6px 28px rgba(0, 0, 0, 0.12), 0 12px 48px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

.toolbar-in-call {
  background: linear-gradient(145deg, #e8f5e9 0%, #f1f8e9 100%);
  border-color: rgba(76, 175, 80, 0.2);
}

/* FAB Style Buttons */
.toolbar-fab {
  width: 56px !important;
  height: 56px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  position: relative;
  overflow: visible !important;
}

.toolbar-fab::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 50%;
  padding: 2px;
  background: linear-gradient(
    145deg,
    rgba(255, 255, 255, 0.5),
    rgba(0, 0, 0, 0.05)
  );
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
}

/* Dialer FAB */
.dialer-fab {
  background: linear-gradient(145deg, #4caf50 0%, #43a047 100%) !important;
  box-shadow: 0 4px 14px rgba(76, 175, 80, 0.35), 0 2px 6px rgba(0, 0, 0, 0.1) !important;
}

.dialer-fab:hover {
  transform: scale(1.08) !important;
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.45), 0 3px 8px rgba(0, 0, 0, 0.12) !important;
}

.dialer-fab:active {
  transform: scale(1.02) !important;
}

.dialer-fab.fab-in-call {
  background: linear-gradient(145deg, #4caf50 0%, #43a047 100%) !important;
  box-shadow: 0 4px 14px rgba(76, 175, 80, 0.4), 0 2px 6px rgba(0, 0, 0, 0.1) !important;
}

.dialer-fab.fab-disconnected {
  background: linear-gradient(145deg, #78909c 0%, #607d8b 100%) !important;
  box-shadow: 0 4px 14px rgba(96, 125, 139, 0.3), 0 2px 6px rgba(0, 0, 0, 0.1) !important;
}

/* Inner icon container */
.fab-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  position: relative;
  z-index: 1;
}

.fab-inner .v-icon {
  color: white !important;
}

/* Pulse ring animation for in-call */
.pulse-ring::before,
.pulse-ring::after {
  content: "";
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 2px solid currentColor;
  opacity: 0;
  animation: pulse-ring 2s ease-out infinite;
}

.pulse-ring::after {
  animation-delay: 1s;
}

@keyframes pulse-ring {
  0% {
    transform: scale(0.8);
    opacity: 0.8;
  }
  100% {
    transform: scale(1.8);
    opacity: 0;
  }
}

/* Tablet Responsive (between 481px and 768px) */
@media (min-width: 481px) and (max-width: 768px) {
  .dialer-toolbar-container {
    bottom: 24px;
    right: 20px;
    margin-right: 0.5rem;
  }

  /* When Crisp chat is visible on tablet, move up */
  .dialer-toolbar-container.crisp-visible:not(.centered-mode) {
    bottom: 90px;
  }

  /* On tablet, sidebar is hidden so center at true 50% */
  .dialer-toolbar-container.centered-mode {
    left: 50% !important;
    right: auto;
    margin-right: 0;
    bottom: 32px;
  }

  .dialer-toolbar {
    gap: 10px;
    padding: 8px 14px;
  }

  .toolbar-fab {
    width: 52px !important;
    height: 52px !important;
  }
}

/* Mobile Responsive (up to 480px) */
@media (max-width: 480px) {
  .dialer-toolbar-container {
    bottom: 20px;
    right: 16px;
  }

  /* When Crisp chat is visible on mobile, move up */
  .dialer-toolbar-container.crisp-visible:not(.centered-mode) {
    bottom: 80px;
  }

  /* On mobile, sidebar is hidden so center at true 50% */
  .dialer-toolbar-container.centered-mode {
    left: 50% !important;
    right: auto;
  }

  .dialer-toolbar {
    gap: 10px;
    padding: 8px 14px;
  }

  .toolbar-fab {
    width: 50px !important;
    height: 50px !important;
  }

  .status-dot {
    width: 12px;
    height: 12px;
    border-width: 2px;
  }
}

@media (max-width: 360px) {
  .dialer-toolbar {
    gap: 8px;
    padding: 6px 12px;
  }

  .toolbar-fab {
    width: 46px !important;
    height: 46px !important;
  }
}

/* Original FAB style for non-CRM pages */
.dialer-fab-simple {
  position: relative !important;
  z-index: 1000;
  transition: all 0.3s ease;
  background: linear-gradient(145deg, #4caf50 0%, #43a047 100%) !important;
  color: white !important;
}

.dialer-fab-simple .v-icon {
  color: white !important;
}

.dialer-fab-simple:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.45) !important;
}

.dialer-fab-simple.fab-in-call {
  animation: pulse-call 2s ease-in-out infinite alternate;
  box-shadow: 0 0 20px rgba(76, 175, 80, 0.5) !important;
}

/* Connection indicator - original style */
.connection-indicator {
  position: absolute;
  top: -16px;
  right: 5px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid white;
  transition: background-color 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  z-index: 10;
}

/* Ensure FAB buttons allow overflow for indicator */
.dialer-fab,
.dialer-fab-simple {
  overflow: visible !important;
}

.dialer-fab .v-btn__content,
.dialer-fab-simple .v-btn__content {
  overflow: visible !important;
}

.indicator-disconnected {
  background-color: #9e9e9e;
}

.indicator-connecting {
  background-color: #ff9800;
  animation: blink 1s ease-in-out infinite alternate;
}

.indicator-connected {
  background-color: #2196f3;
}

.indicator-registered {
  background-color: #4caf50;
}

.indicator-error {
  background-color: #f44336;
  animation: blink 0.5s ease-in-out infinite alternate;
}

@keyframes pulse-call {
  from {
    transform: scale(1);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2), 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  to {
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3), 0 0 0 10px rgba(76, 175, 80, 0);
  }
}

@keyframes blink {
  from {
    opacity: 1;
  }
  to {
    opacity: 0.5;
  }
}

/* Dark Theme Styles */
.theme--dark .dialer-toolbar {
  background: linear-gradient(145deg, #2d2d2d 0%, #1e1e1e 100%) !important;
  border-color: rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3), 0 8px 40px rgba(0, 0, 0, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.theme--dark .dialer-toolbar:hover {
  box-shadow: 0 6px 28px rgba(0, 0, 0, 0.4), 0 12px 48px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
}

.theme--dark .toolbar-in-call {
  background: linear-gradient(145deg, #1b3a1b 0%, #0d290d 100%) !important;
  border-color: rgba(76, 175, 80, 0.3);
}
</style>
